import { CalendarCheck, Flame, Images, Inbox, Lightbulb, ListChecks, LucideIcon, Sparkles } from 'lucide-react';

import type { DecisionActionId, DecisionItem, DecisionSource, Urgency } from '../../../common/hooks/use-decisions-api.hook';

/**
 * How the decisions inbox reads: which glyph a source gets, what each button
 * says for each source, and how rows group. Pure, for the tests; the words
 * themselves are under DECISIONS in src/i18n/decisions.
 */

export const URGENCY_ORDER: Urgency[] = ['now', 'today', 'week'];

/** Neutral line glyphs — colour is for meaning, not for telling sources apart. */
export const SOURCE_ICON: Record<DecisionSource, LucideIcon> = {
  request: Inbox,
  lead: Flame,
  assistant: Sparkles,
  agent_run: CalendarCheck,
  content_pack: Images,
  insight: Lightbulb,
  setup: ListChecks,
};

/** The order sources are offered as filters and listed in the empty state. */
export const SOURCE_ORDER: DecisionSource[] = ['request', 'lead', 'assistant', 'agent_run', 'content_pack', 'insight', 'setup'];

export const sourceKey = (source: DecisionSource) => `DECISIONS.SOURCE.${source.toUpperCase()}`;

/** What a button says: "Mark contacted" on a buyer, "Publish" on a post, "Dismiss" on a finding. */
export function actionLabelKey(item: Pick<DecisionItem, 'source' | 'kind' | 'meta'>, action: DecisionActionId): string {
  switch (action) {
    case 'approve':
      return item.meta?.sideEffect === 'publish' ? 'DECISIONS.ACTION.PUBLISH' : 'DECISIONS.ACTION.APPROVE';
    case 'decline':
      return item.source === 'insight' || item.source === 'setup' ? 'DECISIONS.ACTION.DISMISS' : 'DECISIONS.ACTION.DECLINE';
    case 'done':
      return 'DECISIONS.ACTION.CONTACTED';
    case 'snooze':
      return 'DECISIONS.ACTION.SNOOZE';
    default:
      return 'DECISIONS.ACTION.OPEN';
  }
}

/** What the "handled" line says once it is done. */
export function handledKey(item: Pick<DecisionItem, 'source'>, action: DecisionActionId): string {
  if (action === 'decline' && (item.source === 'insight' || item.source === 'setup')) return 'DECISIONS.HANDLED.DISMISSED';
  return `DECISIONS.HANDLED.${action.toUpperCase()}`;
}

/** The buttons drawn on a row: the decisions themselves, then "open". */
export function rowActions(item: DecisionItem) {
  const decide = item.actions.filter((a) => a.id !== 'open');
  return {
    primary: decide.filter((a) => a.kind === 'primary').map((a) => a.id),
    secondary: decide.filter((a) => a.kind === 'secondary').map((a) => a.id),
    // A finding's "open" is its yes: drawn as the primary button.
    openIsPrimary: item.actions.some((a) => a.id === 'open' && a.kind === 'primary'),
  };
}

export function groupByUrgency(items: DecisionItem[]): { urgency: Urgency; items: DecisionItem[] }[] {
  return URGENCY_ORDER.map((urgency) => ({ urgency, items: items.filter((i) => i.urgency === urgency) })).filter((g) => g.items.length);
}

export function filterBySource(items: DecisionItem[], source: DecisionSource | 'all'): DecisionItem[] {
  return source === 'all' ? items : items.filter((i) => i.source === source);
}

/** Whole units in the reader's language: "2 162 €", "€2,162". */
export function formatMoney(value: { amount: number; currency: string } | undefined, lang: string): string | null {
  if (!value || typeof value.amount !== 'number') return null;
  try {
    return new Intl.NumberFormat(lang === 'ar' ? 'ar-u-nu-latn' : lang, { style: 'currency', currency: value.currency, maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(value.amount);
  } catch {
    return `${Math.round(value.amount)} ${value.currency}`;
  }
}

/** Internal links stay in the app; an absolute one (a page of the vendor's site) opens in a tab. */
export const isExternal = (href: string) => /^https?:\/\//.test(href);
